import { GridPosition, Action } from '../types/interfaces';
import { ActionType } from '../types/enums';
import { Actor } from './actor';

// A generic object pool for reusing objects
export class ObjectPool<T> {
  private pool: T[] = [];
  private factory: () => T;
  private reset: (obj: T) => void;
  private index = 0;

  constructor(factory: () => T, reset: (obj: T) => void) {
    this.factory = factory;
    this.reset = reset;
  }

  // Get an object from the pool or create a new one
  public get(): T {
    if (this.index >= this.pool.length) {
      this.pool.push(this.factory());
    }
    return this.pool[this.index++];
  }

  // Reset the pool for reuse
  public resetPool(): void {
    for (let i = 0; i < this.index; i++) {
      this.reset(this.pool[i]);
    }
    this.index = 0;
  }
}

// Utility for creating and reusing grid positions
export class GridPositionPool {
  private static instance: GridPositionPool;
  private pool: ObjectPool<GridPosition>;

  private constructor() {
    this.pool = new ObjectPool<GridPosition>(
      () => ({ x: 0, y: 0 }),
      (pos) => { pos.x = 0; pos.y = 0; }
    );
  }

  public static getInstance(): GridPositionPool {
    if (!GridPositionPool.instance) {
      GridPositionPool.instance = new GridPositionPool();
    }
    return GridPositionPool.instance;
  }

  public get(x: number, y: number): GridPosition {
    const pos = this.pool.get();
    pos.x = x;
    pos.y = y;
    return pos;
  }

  public resetPool(): void {
    this.pool.resetPool();
  }
}

// Action pool for reusing common actions
export class ActionPool {
  private static instance: ActionPool;
  private moveActionPool: ObjectPool<Action>;
  private waitActionPool: ObjectPool<Action>;
  private attackActionPool: ObjectPool<Action>;

  private constructor() {
    // Move action pool
    this.moveActionPool = new ObjectPool<Action>(
      () => ({
        type: ActionType.Move,
        actor: null,
        target: { x: 0, y: 0 },
        execute: function() {
          const actor = this.actor as Actor;
          console.log(`${actor.constructor.name} moves to (${this.target.x}, ${this.target.y})`);
          actor.moveToGrid(this.target.x, this.target.y);
          actor.actionPoints--;
        }
      }),
      (action) => { 
        action.actor = null; 
        action.target.x = 0;
        action.target.y = 0;
      }
    );

    // Wait action pool
    this.waitActionPool = new ObjectPool<Action>(
      () => ({
        type: ActionType.Wait,
        actor: null,
        execute: function() {
          const actor = this.actor as Actor;
          console.log(`${actor.constructor.name} waits`);
          actor.actionPoints--;
        }
      }),
      (action) => { action.actor = null; }
    );

    // Attack action pool
    this.attackActionPool = new ObjectPool<Action>(
      () => ({
        type: ActionType.Attack,
        actor: null,
        target: { x: 0, y: 0 },
        targetActor: null,
        execute: function() {
          const actor = this.actor as Actor;
          console.log(`${actor.constructor.name} attacks (${this.target.x}, ${this.target.y})`);
          // Attack logic will be set when getting from pool
          actor.actionPoints--;
        }
      }),
      (action) => { 
        action.actor = null; 
        action.target.x = 0;
        action.target.y = 0;
        action.targetActor = null;
      }
    );
  }

  public static getInstance(): ActionPool {
    if (!ActionPool.instance) {
      ActionPool.instance = new ActionPool();
    }
    return ActionPool.instance;
  }

  public getMoveAction(actor: Actor, targetX: number, targetY: number): Action {
    const action = this.moveActionPool.get();
    action.actor = actor;
    action.target.x = targetX;
    action.target.y = targetY;
    return action;
  }

  public getWaitAction(actor: Actor): Action {
    const action = this.waitActionPool.get();
    action.actor = actor;
    return action;
  }

  public getAttackAction(actor: Actor, targetX: number, targetY: number, attackLogic: () => void): Action {
    const action = this.attackActionPool.get();
    action.actor = actor;
    action.target.x = targetX;
    action.target.y = targetY;
    action.execute = function() {
      attackLogic();
      actor.actionPoints--;
    };
    return action;
  }

  public resetPools(): void {
    this.moveActionPool.resetPool();
    this.waitActionPool.resetPool();
    this.attackActionPool.resetPool();
  }
}